import React from "react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal } from "../components/Reveal";
import { PageHero, CtaBand, Overline } from "../components/Primitives";
import { IMG } from "../lib/assets";
import { Seo } from "../components/Seo";

export default function Method() {
  const { t } = useLang();
  const dims = t("home.dimensions");
  const imgs = [IMG.structural, IMG.inner, IMG.metabolic];
  const blocks = [
    { title: t("method.howTitle"), text: t("method.howText") },
    { title: t("method.assessmentTitle"), text: t("method.assessmentText") },
    { title: t("method.sequencingTitle"), text: t("method.sequencingText") },
    { title: t("method.autonomyTitle"), text: t("method.autonomyText") },
  ];

  return (
    <div data-testid="method-page">
      <Seo pageKey="method" path="/method" />
      <PageHero overline={t("method.heroOverline")} title={t("method.title")} intro={t("method.intro")} image={IMG.method} />

      {/* Dimensions recap */}
      <section className="relative py-16 md:py-24 border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal><Overline>{t("home.dimensionsOverline")}</Overline></Reveal>
          <div className="mt-14 space-y-px bg-white/10 border border-white/10">
            {dims.map((d, i) => (
              <Reveal key={i} delay={i * 0.08} className="group grid grid-cols-1 md:grid-cols-12 gap-6 bg-kenosis-black p-8 md:p-12">
                <div className="md:col-span-1"><span className="font-serif text-3xl text-kenosis-gold">{d.num}</span></div>
                <div className="md:col-span-4">
                  <h3 className="font-serif text-2xl md:text-3xl font-normal text-kenosis-ivory">{d.title}</h3>
                  <p className="mt-4 font-sans text-sm font-light text-kenosis-muted">{d.purpose}</p>
                </div>
                <div className="md:col-span-4">
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {d.tools.map((tool, k) => (
                      <li key={k} className="flex items-center gap-2 font-sans text-xs text-kenosis-ivory/60">
                        <span className="h-1 w-1 rounded-full bg-kenosis-gold/70" />{tool}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="md:col-span-3">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={imgs[i]} alt={d.title} className="h-full w-full object-cover opacity-70 transition-transform duration-[1.2s] group-hover:scale-105" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Logic blocks */}
      <section className="relative py-8 md:py-16">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          {blocks.map((b, i) => (
            <Reveal key={i} delay={0.05} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 border-t border-white/10 py-14 md:py-20">
              <div className="md:col-span-5 flex items-start gap-6">
                <span className="font-serif text-3xl text-kenosis-gold/40">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-serif text-3xl md:text-4xl font-light leading-tight tracking-tight text-kenosis-ivory">{b.title}</h2>
              </div>
              <p className="md:col-span-7 self-center font-sans text-base md:text-lg font-light leading-relaxed text-kenosis-muted">{b.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="border border-kenosis-gold/20 bg-kenosis-surface p-8 md:p-12">
            <p className="font-sans text-sm font-light leading-relaxed text-kenosis-ivory/70">{t("method.note")}</p>
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
